import React, { useState, useEffect } from "react";
import { Home as HomeIcon, Recycle, HandCoins, Landmark, Gavel, ChevronDown, ArrowRight, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { PageHero, Eyebrow, SectionTitle, GoldButton } from "@/components/shared";
import { Ornament } from "@/components/Ornament";
import { RaeumungForm } from "@/components/RaeumungForm";
import { MarketplaceModule } from "@/components/Marketplace";
import { servicesContent, serviceLabels } from "@/i18n/services";
import { scrollToId } from "@/lib/scroll";
import { IMAGES } from "@/data/treasures";

const ICONS = { raeumung: HomeIcon, entsorgung: Recycle, ankauf: HandCoins, nachlass: Landmark, beratung: Gavel };

const FaqItem = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 py-4 text-left group">
        <span className="font-sans text-espresso font-medium group-hover:text-gold-deep transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-gold-deep shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-400 ${open ? "max-h-60 pb-5" : "max-h-0"}`}>
        <p className="text-espresso/65 font-sans text-sm leading-relaxed pr-8">{a}</p>
      </div>
    </div>
  );
};

const DienstleistungenPage = () => {
  const { t, lang, contact } = useLang();
  const ref = useReveal([lang]);
  const services = servicesContent[lang];
  const labels = serviceLabels[lang];
  const [active, setActive] = useState(services[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    services.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [services]);

  return (
    <div ref={ref}>
      <PageHero image={IMAGES.servicesImage} eyebrow={t.services.eyebrow} title={t.services.title} lead={t.services.lead} />

      {/* Split: sticky anchor nav + detailed sections */}
      <section className="py-20 md:py-28 bg-cream grain-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[260px_1fr] gap-12">
          {/* Sticky overview nav */}
          <aside className="lg:sticky lg:top-28 h-max" data-testid="services-nav">
            <span className="text-[11px] tracking-[0.3em] uppercase text-gold-deep">{labels.overview}</span>
            <Ornament className="my-4 justify-start opacity-60" width={140} />
            <nav className="flex lg:flex-col gap-2 flex-wrap">
              {services.map((s) => {
                const Icon = ICONS[s.id];
                return (
                  <button key={s.id} onClick={() => scrollToId(s.id)} data-testid={`servicenav-${s.id}`}
                    className={`group flex items-center gap-3 px-4 py-3 rounded-xl text-left font-sans text-sm transition-all duration-300 ${
                      active === s.id ? "bg-espresso text-gold" : "text-espresso/70 hover:bg-sand hover:text-espresso"
                    }`}>
                    <Icon className={`w-4 h-4 shrink-0 ${active === s.id ? "text-gold" : "text-gold-deep"}`} />
                    {s.title}
                  </button>
                );
              })}
            </nav>
            <GoldButton as="button" onClick={() => scrollToId("anfrage")} data-testid="nav-request-cta" className="mt-6 w-full justify-center !px-5 !py-3 text-xs">
              {labels.request}
            </GoldButton>
            <a href={`tel:${contact.phoneHref}`} data-testid="nav-call-cta"
              className="group relative mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-white text-xs font-semibold tracking-wide overflow-hidden animate-glow-pulse-green transition-all duration-300 hover:bg-[#1ebe57] hover:scale-[1.03] active:scale-95">
              <span className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out -skew-x-12 bg-gradient-to-r from-transparent via-white/35 to-transparent" />
              <Phone className="w-4 h-4 relative z-10" /> <span className="relative z-10">{labels.call}</span>
            </a>
          </aside>

          {/* Service sections */}
          <div className="space-y-24">
            {services.map((s, idx) => {
              const Icon = ICONS[s.id];
              return (
                <article key={s.id} id={s.id} className="scroll-mt-28" data-testid={`service-section-${s.id}`}>
                  <div data-reveal className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-espresso flex items-center justify-center shrink-0"><Icon className="w-6 h-6 text-gold" /></div>
                    <div>
                      <span className="block text-[11px] tracking-[0.3em] uppercase text-gold-deep">{String(idx + 1).padStart(2, "0")} · {s.tagline}</span>
                      <h2 className="font-serif text-3xl md:text-4xl text-espresso leading-tight">{s.title}</h2>
                    </div>
                  </div>
                  <p data-reveal className="text-espresso/70 text-lg font-sans leading-relaxed max-w-2xl">{s.intro}</p>

                  {/* Process */}
                  <div className="mt-10" data-reveal>
                    <h3 className="font-serif text-xl text-espresso mb-5">{labels.process}</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {s.steps.map((st, i) => (
                        <div key={i} className="relative bg-sand rounded-xl p-5 border border-border">
                          <span className="absolute top-3 right-4 font-serif text-3xl text-gold/20">{i + 1}</span>
                          <h4 className="font-sans font-semibold text-espresso pr-8">{st.t}</h4>
                          <p className="mt-2 text-espresso/60 text-sm font-sans leading-relaxed">{st.d}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* FAQ */}
                  <div className="mt-10" data-reveal>
                    <h3 className="font-serif text-xl text-espresso mb-2">{labels.faq}</h3>
                    <div>
                      {s.faq.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} testid={`faq-${s.id}-${i}`} />)}
                    </div>
                  </div>

                  {/* Per-service CTA */}
                  <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl bg-espresso p-6" data-reveal>
                    <p className="flex-1 min-w-[200px] font-serif text-lg text-cream">{s.tagline} – {labels.request.toLowerCase()}?</p>
                    <div className="flex flex-wrap gap-3">
                      <GoldButton as="button" onClick={() => scrollToId("anfrage")} data-testid={`cta-request-${s.id}`} className="!px-6 !py-3 text-xs">{labels.request} <ArrowRight className="w-4 h-4" /></GoldButton>
                      <a href={`tel:${contact.phoneHref}`} data-testid={`cta-call-${s.id}`} className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-6 py-3 text-gold text-xs tracking-wide transition-all duration-300 hover:bg-gold hover:text-espresso-deep hover:shadow-[0_0_22px_rgba(200,169,126,0.5)] active:scale-95"><Phone className="w-4 h-4" /> {contact.phone}</a>
                    </div>
                  </div>

                  <Ornament className="mt-14 justify-start opacity-40" width={220} />
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust line */}
      <section className="py-16 bg-sand text-center">
        <div className="max-w-2xl mx-auto px-6" data-reveal>
          <p className="text-espresso/70 font-sans leading-relaxed italic">{t.servicesMarket}</p>
        </div>
      </section>

      {/* Full clearance request form */}
      <RaeumungForm />

      {/* Trust band */}
      <section className="relative py-24 md:py-32 bg-espresso-deep grain-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMAGES.storyImage})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-espresso-deep/75" />
        <div className="relative max-w-3xl mx-auto px-6 text-center" data-reveal>
          <span className="text-[11px] tracking-[0.3em] uppercase text-gold/70">Mara Services Gruppe</span>
          <SectionTitle dark className="mt-4">{t.services.title}</SectionTitle>
          <p className="mt-6 text-cream/80 text-lg font-sans leading-relaxed">{t.services.trust}</p>
          <Ornament className="mt-9 opacity-70" width={220} />
        </div>
      </section>

      <MarketplaceModule />
    </div>
  );
};

export default DienstleistungenPage;
